import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ChefHat, Sparkles, ArrowRight, ShieldCheck, TrendingUp, Zap, LayoutDashboard, ImagePlus, Undo2, Utensils, Crosshair, Clock, Eye, FileText, Boxes, LineChart, Calculator, AlertTriangle, Plus, X, ScanLine, Building2, Lock, Database, Rocket, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { detectCurrency } from "@/lib/detectCurrency";
import EnterpriseAuditModal from "@/components/EnterpriseAuditModal";

const REF_STORAGE_KEY = "cs_ref";
const DEMO_NAME_KEY = "cs_demo_dish_name";
const DEMO_PHOTO_KEY = "cs_demo_dish_photo";
const DEMO_INGREDIENTS_KEY = "cs_demo_dish_ingredients";
const MAX_PHOTO_BYTES = 900_000; // ~900KB: safe for localStorage

// Fine-dining pan-seared salmon with greens: verified Unsplash editorial IDs.
// Displayed by default in the "Your Dish Photo" frame. onError falls through
// to the second reliable shot so the demo never renders a broken image.
const DEFAULT_HERO_PHOTO = "https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=900&q=85";
const DEFAULT_HERO_PHOTO_FALLBACK = "https://images.unsplash.com/photo-1560717845-968823efbee1?auto=format&fit=crop&w=900&q=85";

// --------------------------------------------------------------------------
// Signature Atlantic Salmon: pre-filled recipe used only for the public demo.
// Per-portion quantities. Purchase prices are in the given "per_unit" bucket.
// yield_pct handles trim/loss so cost math reflects reality, not raw weight.
// --------------------------------------------------------------------------
const SIGNATURE_RECIPE = {
  name: "Signature Atlantic Salmon",
  sell_price: 42.0, // per portion: used to derive margin %
  ingredients: [
    { id: "salmon", name: "Atlantic Salmon Fillet", qty: 180, unit: "g", per_unit: 1000, price: 58.0, yield_pct: 92 },
    { id: "butter", name: "Cultured Butter",         qty: 30,  unit: "g", per_unit: 1000, price: 18.0, yield_pct: 100 },
    { id: "dill",   name: "Fresh Dill",              qty: 3,   unit: "g", per_unit: 1000, price: 60.0, yield_pct: 88 },
    { id: "lemon",  name: "Meyer Lemon",             qty: 15,  unit: "g", per_unit: 1000, price: 6.0,  yield_pct: 65 },
    { id: "greens", name: "Micro-Greens",            qty: 5,   unit: "g", per_unit: 1000, price: 80.0, yield_pct: 95 },
  ],
};

// Unit presets: per_unit is the price basis (e.g. 1000g = $58/kg), basis is the
// label the visitor reads next to the price input, kind is what the backend
// stores on the pantry ingredient when the recipe is carried into an account.
const UNIT_PRESETS = {
  g:    { per_unit: 1000, basis: "$/kg",   kind: "weight" },
  kg:   { per_unit: 1,    basis: "$/kg",   kind: "weight" },
  ml:   { per_unit: 1000, basis: "$/L",    kind: "volume" },
  each: { per_unit: 1,    basis: "$/each", kind: "count"  },
};
const UNIT_OPTIONS = ["g", "kg", "ml", "each"];

function unitPreset(unit) {
  return UNIT_PRESETS[unit] || UNIT_PRESETS.g;
}

// Cost per portion = sum(qty/per_unit * price / (yield/100))
function computeCostPerPortion(ingredients) {
  return ingredients.reduce((acc, i) => {
    const y = Math.max(1, Number(i.yield_pct) || 100);
    const p = Math.max(0, Number(i.price) || 0);
    return acc + (i.qty / i.per_unit) * p / (y / 100);
  }, 0);
}

// --------------------------------------------------------------------------
// AnimatedNumber: smooth easing between old and new value. No deps.
// Duration ~360ms, ease-out cubic. Preserves decimals via `decimals` prop.
// --------------------------------------------------------------------------
function AnimatedNumber({ value, decimals = 2, prefix = "", suffix = "", className = "", testId }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  const fromRef = useRef(value);
  const startRef = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    fromRef.current = display;
    startRef.current = performance.now();
    const to = value;
    const dur = 380;
    const tick = (now) => {
      const t = Math.min(1, (now - startRef.current) / dur);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const next = fromRef.current + (to - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplay(to);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const formatted = Number(display).toFixed(decimals);
  return (
    <span className={className} data-testid={testId}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

// --------------------------------------------------------------------------
// GlassPanel: bespoke V2-True-Glass panel with a 44px blur, chromatic
// prism hairline, top specular light-catch and a 100px long-throw shadow.
// Overrides the default .glass class for the marquee landing panel only.
// --------------------------------------------------------------------------
function GlassPanel({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative rounded-[28px] ${className}`}
      style={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0.02) 100%)",
        backdropFilter: "blur(44px) saturate(200%)",
        WebkitBackdropFilter: "blur(44px) saturate(200%)",
        border: "1px solid rgba(255,255,255,0.13)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.34)", // top specular
          "inset 0 -1px 0 rgba(255,255,255,0.04)",
          "0 100px 140px -40px rgba(0,0,0,0.9)",  // long-throw floor
          "0 40px 80px -20px rgba(0,0,0,0.75)",
          "0 0 0 1px rgba(255,255,255,0.03)",
        ].join(","),
      }}
    >
      {/* Chromatic prism hairline: gold → white → teal */}
      <div
        aria-hidden
        className="absolute top-0 left-6 right-6 h-px rounded-full pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(232,207,154,0.9), rgba(255,255,255,0.6), rgba(90,178,201,0.85), transparent)",
        }}
      />
      {/* Diagonal specular streak */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-[28px] pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 32%, transparent 70%, rgba(255,255,255,0.06) 100%)",
          mixBlendMode: "screen",
          opacity: 0.7,
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Live Costing Simulator: the interactive proof-of-value demo.
// --------------------------------------------------------------------------
function LiveCostingSimulator() {
  const [portions, setPortions] = useState(1);
  const [ingredients, setIngredients] = useState(SIGNATURE_RECIPE.ingredients);
  const [dishName, setDishName] = useState(SIGNATURE_RECIPE.name);
  const [heroPhoto, setHeroPhoto] = useState(null); // data URL
  const [photoError, setPhotoError] = useState("");
  const fileInputRef = useRef(null);

  // Restore any prior customisation so the demo feels persistent between visits.
  useEffect(() => {
    try {
      const savedName = localStorage.getItem(DEMO_NAME_KEY);
      const savedPhoto = localStorage.getItem(DEMO_PHOTO_KEY);
      const savedIngredients = localStorage.getItem(DEMO_INGREDIENTS_KEY);
      if (savedName) setDishName(savedName);
      if (savedPhoto) setHeroPhoto(savedPhoto);
      if (savedIngredients) {
        const parsed = JSON.parse(savedIngredients);
        if (Array.isArray(parsed) && parsed.length > 0) setIngredients(parsed);
      }
    } catch (_) { /* localStorage may be unavailable in private-mode */ }
  }, []);

  const persistIngredients = (next) => {
    try { localStorage.setItem(DEMO_INGREDIENTS_KEY, JSON.stringify(next)); } catch (_) {}
  };

  const onNameChange = (v) => {
    setDishName(v);
    try { localStorage.setItem(DEMO_NAME_KEY, v); } catch (_) {}
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) { setPhotoError("Choose an image file."); return; }
    if (file.size > MAX_PHOTO_BYTES) { setPhotoError("Photo too large: pick something under ~900 KB."); return; }
    setPhotoError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result;
      if (typeof url === "string") {
        setHeroPhoto(url);
        try { localStorage.setItem(DEMO_PHOTO_KEY, url); } catch (_) {}
      }
    };
    reader.readAsDataURL(file);
  };

  const resetDemo = () => {
    setDishName(SIGNATURE_RECIPE.name);
    setHeroPhoto(null);
    setPhotoError("");
    setIngredients(SIGNATURE_RECIPE.ingredients);
    try {
      localStorage.removeItem(DEMO_NAME_KEY);
      localStorage.removeItem(DEMO_PHOTO_KEY);
      localStorage.removeItem(DEMO_INGREDIENTS_KEY);
    } catch (_) {}
  };

  const ingredientsCustomised = useMemo(() => {
    if (ingredients.length !== SIGNATURE_RECIPE.ingredients.length) return true;
    return ingredients.some((row, idx) => {
      const base = SIGNATURE_RECIPE.ingredients[idx];
      return !base
        || row.name !== base.name
        || Number(row.qty) !== base.qty
        || Number(row.price) !== base.price
        || Number(row.yield_pct) !== base.yield_pct;
    });
  }, [ingredients]);

  const isCustomised = dishName !== SIGNATURE_RECIPE.name || !!heroPhoto || ingredientsCustomised;

  const costPerPortion = useMemo(() => computeCostPerPortion(ingredients), [ingredients]);
  const totalCost = costPerPortion * portions;
  const totalRevenue = SIGNATURE_RECIPE.sell_price * portions;
  const marginPct = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : 0;
  const marginDollars = totalRevenue - totalCost;

  const updateField = (id, field, value) => {
    const clean = value === "" ? 0 : Number(value);
    setIngredients((prev) => {
      const next = prev.map((i) => (i.id === id ? { ...i, [field]: clean } : i));
      persistIngredients(next);
      return next;
    });
  };

  const updateName = (id, value) => {
    setIngredients((prev) => {
      const next = prev.map((i) => (i.id === id ? { ...i, name: value } : i));
      persistIngredients(next);
      return next;
    });
  };

  const updateUnit = (id, unit) => {
    const preset = unitPreset(unit);
    setIngredients((prev) => {
      const next = prev.map((i) =>
        i.id === id ? { ...i, unit, per_unit: preset.per_unit } : i
      );
      persistIngredients(next);
      return next;
    });
  };

  const addIngredient = () => {
    setIngredients((prev) => {
      const next = [
        ...prev,
        {
          id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          name: "",
          qty: 100,
          unit: "g",
          per_unit: 1000,
          price: 10.0,
          yield_pct: 100,
        },
      ];
      persistIngredients(next);
      return next;
    });
  };

  const removeIngredient = (id) => {
    setIngredients((prev) => {
      if (prev.length <= 1) return prev; // never leave the grid empty
      const next = prev.filter((i) => i.id !== id);
      persistIngredients(next);
      return next;
    });
  };

  return (
    <GlassPanel className="p-6 md:p-10" testId="live-costing-simulator">
      {/* Top row: eyebrow + editable name + description on the left, headline
          metrics on the right. Photo lives BELOW alongside the ingredient
          grid so the two align vertically. */}
      <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-[0.55rem] uppercase tracking-[0.28em] flex items-center gap-1.5" style={{ color: "rgba(232,207,154,0.9)" }}>
              <Sparkles className="w-3 h-3" /> Live Costing Simulator
            </div>
            {isCustomised && (
              <button
                type="button"
                onClick={resetDemo}
                data-testid="reset-demo-btn"
                className="text-[0.55rem] uppercase tracking-[0.22em] text-white/40 hover:text-white/80 transition-colors flex items-center gap-1"
                aria-label="Reset to Signature Atlantic Salmon"
              >
                <Undo2 className="w-2.5 h-2.5" /> Reset
              </button>
            )}
          </div>
          <input
            type="text"
            value={dishName}
            onChange={(e) => onNameChange(e.target.value)}
            maxLength={80}
            placeholder="Name your signature dish"
            data-testid="dish-name-input"
            className="mt-2 w-full bg-transparent font-display text-3xl md:text-4xl leading-tight text-white/95 focus:outline-none focus:ring-0 border-b border-transparent focus:border-[rgba(232,207,154,0.35)] transition-colors"
          />
          <p className="text-xs text-white/55 mt-2 max-w-md">
            {isCustomised
              ? "That's YOUR dish now. Edit the prices below to see what it actually costs you."
              : "Edit any market price or yield below: watch the plate cost and margin recalculate in real time."}
          </p>
        </div>

        {/* Headline metrics: glass panels that refract the salmon photo colour */}
        <div className="flex gap-4 flex-wrap">
          <div
            className="rounded-xl px-4 py-3"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03))",
              border: "1px solid rgba(255,255,255,0.14)",
              backdropFilter: "blur(44px) saturate(200%)",
              WebkitBackdropFilter: "blur(44px) saturate(200%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.20), 0 12px 40px -8px rgba(0,0,0,0.5)",
              minWidth: 178,
            }}
          >
            <div className="text-[0.55rem] uppercase tracking-[0.24em] text-white/60">Plate cost</div>
            <div className="font-display text-3xl tabular-nums text-white/95 mt-0.5">
              <AnimatedNumber value={costPerPortion} decimals={4} prefix="$" testId="metric-plate-cost" />
            </div>
            <div className="text-[0.65rem] text-white/50 mt-0.5">per portion : food only</div>
          </div>
          <div
            className="rounded-xl px-4 py-3"
            style={{
              background: "linear-gradient(180deg, rgba(232,207,154,0.20), rgba(232,207,154,0.05))",
              border: "1px solid rgba(232,207,154,0.34)",
              backdropFilter: "blur(44px) saturate(200%)",
              WebkitBackdropFilter: "blur(44px) saturate(200%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.22), 0 12px 40px -8px rgba(200,169,106,0.28)",
              minWidth: 178,
            }}
          >
            <div className="text-[0.55rem] uppercase tracking-[0.24em]" style={{ color: "rgba(232,207,154,0.9)" }}>Margin</div>
            <div className="font-display text-3xl tabular-nums mt-0.5" style={{ color: "#f2dfb5" }}>
              <AnimatedNumber value={marginPct} decimals={1} suffix="%" testId="metric-margin-pct" />
            </div>
            <div className="text-[0.65rem] text-white/55 mt-0.5">
              <AnimatedNumber value={marginDollars} decimals={2} prefix="$" testId="metric-margin-dollars" /> / batch
            </div>
          </div>
        </div>
      </div>

      {/* Body: tall fine-dining photo on the LEFT, ingredient grid on the RIGHT.
          items-stretch makes the photo column match the exact height of the
          ingredient list so the salmon plate sits alongside every row from
          Atlantic Salmon Fillet down through Micro-Greens. */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6 items-stretch">
        <div className="lg:w-64 flex-shrink-0 min-h-[360px] relative">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickPhoto}
            data-testid="dish-photo-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="dish-photo-btn"
            className="relative w-full h-full min-h-[360px] rounded-2xl overflow-hidden group transition-all"
            style={{
              background: "transparent",
              border: "1px solid rgba(232,207,154,0.35)",
              boxShadow: "0 0 0 1px rgba(232,207,154,0.28), 0 24px 60px -12px rgba(232,207,154,0.30), inset 0 1px 0 rgba(255,255,255,0.20)",
            }}
            aria-label="Change dish photo"
          >
            <img
              src={heroPhoto || DEFAULT_HERO_PHOTO}
              alt={heroPhoto ? "Your dish" : "Pan-seared Atlantic salmon plated with lemon and micro-greens"}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="dish-photo-img"
              onError={(e) => {
                if (e.currentTarget.src !== DEFAULT_HERO_PHOTO_FALLBACK) {
                  e.currentTarget.src = DEFAULT_HERO_PHOTO_FALLBACK;
                }
              }}
            />
            {/* Warm gold light-catch on top rim */}
            <div
              aria-hidden
              className="absolute inset-x-0 top-0 h-1/3 pointer-events-none"
              style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.15), transparent)" }}
            />
            {/* Bottom vignette so the dish caption stays legible */}
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
              style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.62), transparent)" }}
            />
            {/* Caption pinned to the photo */}
            <div className="absolute inset-x-0 bottom-0 p-4 text-left">
              <div className="text-[0.55rem] uppercase tracking-[0.24em]" style={{ color: "rgba(232,207,154,0.95)" }}>
                Fine dining reference
              </div>
              <div className="font-display text-lg text-white/95 leading-tight mt-1">
                Pan-Seared Atlantic Salmon
              </div>
              <div className="text-[0.65rem] text-white/65 mt-0.5">
                Cultured butter : dill : Meyer lemon : micro-greens
              </div>
            </div>
            {/* Hover state: hint to change */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center">
              <ImagePlus className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
          {photoError && (
            <div className="mt-1 text-[0.6rem] text-red-300 max-w-[16rem]" data-testid="dish-photo-error">{photoError}</div>
          )}
        </div>

        {/* Right column: ingredient grid */}
        <div className="flex-1 min-w-0">

      {/* Ingredients grid: editable, strict 5-column layout with standardized
          numeric input widths. Every numeric input in a column has the SAME
          width and every value right-aligns to the column right edge, so the
          numerals stack cleanly like an accountant's ledger. */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div
              className="grid gap-3 px-4 py-2.5 text-[0.55rem] uppercase tracking-[0.24em] text-white/45 border-b"
              style={{
                gridTemplateColumns: "minmax(180px,1.5fr) 148px 168px 100px 100px",
                borderColor: "rgba(255,255,255,0.06)",
              }}
            >
              <div>Ingredient</div>
              <div className="text-right">Per portion</div>
              <div className="text-right">Market price</div>
              <div className="text-right">Yield %</div>
              <div className="text-right">Cost</div>
            </div>
        {ingredients.map((i) => {
          const rowCost = (i.qty / i.per_unit) * (i.price || 0) / ((i.yield_pct || 100) / 100);
          return (
            <div
              key={i.id}
              className="group relative grid gap-3 px-4 py-3 items-center border-b last:border-b-0 text-sm text-white/90 transition-colors hover:bg-white/[0.02]"
              style={{
                gridTemplateColumns: "minmax(180px,1.5fr) 148px 168px 100px 100px",
                borderColor: "rgba(255,255,255,0.04)",
              }}
              data-testid={`sim-row-${i.id}`}
            >
              {/* Col 1: Ingredient name */}
              <div className="min-w-0">
                <input
                  type="text"
                  value={i.name}
                  onChange={(e) => updateName(i.id, e.target.value)}
                  placeholder="Ingredient name"
                  maxLength={60}
                  className="w-full bg-transparent font-medium text-white/95 text-sm px-2 py-1 -ml-2 rounded border border-transparent hover:border-white/[0.08] focus:border-[rgba(232,207,154,0.35)] focus:bg-white/[0.03] focus:outline-none transition-colors"
                  data-testid={`sim-name-${i.id}`}
                  aria-label="Ingredient name"
                />
              </div>
              {/* Col 2: Per portion. Qty + unit are merged into ONE bordered
                  field so the entire group right-aligns on the same rail as
                  the price, yield and cost values. */}
              <div className="flex justify-end">
                <div
                  className="inline-flex items-stretch rounded overflow-hidden focus-within:ring-1 focus-within:ring-[rgba(232,207,154,0.35)]"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={i.qty}
                    onChange={(e) => updateField(i.id, "qty", e.target.value)}
                    className="w-20 h-8 bg-transparent border-none px-2 text-right tabular-nums text-sm text-white/95 focus:outline-none"
                    data-testid={`sim-qty-${i.id}`}
                    aria-label="Quantity per portion"
                  />
                  <div className="w-px my-1.5" style={{ background: "rgba(255,255,255,0.10)" }} />
                  <div className="relative">
                    <select
                      value={i.unit}
                      onChange={(e) => updateUnit(i.id, e.target.value)}
                      aria-label="Unit"
                      data-testid={`sim-unit-${i.id}`}
                      className="h-8 w-14 bg-transparent border-none pl-2 pr-5 text-xs tabular-nums cursor-pointer appearance-none text-white/95 focus:outline-none"
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u} style={{ background: "#111114", color: "#f5efe0" }}>
                          {u}
                        </option>
                      ))}
                    </select>
                    <svg
                      aria-hidden
                      viewBox="0 0 12 12"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none"
                      fill="none"
                      stroke="rgba(232,207,154,0.75)"
                      strokeWidth="1.5"
                    >
                      <path d="M3 4.5L6 7.5L9 4.5" />
                    </svg>
                  </div>
                </div>
              </div>
              {/* Col 3: Market price. $ prefix + per-row basis suffix baked
                  INTO the input so the row is single-line and all numbers
                  sit on the same rail. */}
              <div className="flex justify-end">
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40 text-xs pointer-events-none">$</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={i.price}
                    onChange={(e) => updateField(i.id, "price", e.target.value)}
                    className="h-8 w-36 rounded pl-5 pr-11 text-right tabular-nums text-sm"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "rgba(255,255,255,0.95)",
                    }}
                    data-testid={`sim-price-${i.id}`}
                  />
                  <span
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[0.6rem] text-white/45 tabular-nums pointer-events-none"
                    data-testid={`sim-basis-${i.id}`}
                  >
                    {unitPreset(i.unit).basis.replace("$", "")}
                  </span>
                </div>
              </div>
              {/* Col 4: Yield % (fixed 88px input, right-anchored) */}
              <div className="flex justify-end">
                <div className="relative">
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="100"
                    value={i.yield_pct}
                    onChange={(e) => updateField(i.id, "yield_pct", e.target.value)}
                    className="h-8 w-[5.5rem] rounded pl-2 pr-6 text-right tabular-nums text-sm"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "rgba(255,255,255,0.95)",
                    }}
                    data-testid={`sim-yield-${i.id}`}
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 text-xs pointer-events-none">%</span>
                </div>
              </div>
              {/* Col 5: Cost (4-decimal precision, right-aligned to column edge) */}
              <div className="text-right tabular-nums text-white/90 text-sm">
                <AnimatedNumber value={rowCost} decimals={4} prefix="$" testId={`sim-cost-${i.id}`} />
              </div>
              {/* Remove row (hover-visible). Never disable the last row: state guard handles that. */}
              <button
                type="button"
                onClick={() => removeIngredient(i.id)}
                data-testid={`sim-remove-${i.id}`}
                aria-label={`Remove ${i.name || "ingredient"}`}
                disabled={ingredients.length <= 1}
                className="absolute -right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all disabled:cursor-not-allowed disabled:opacity-0"
                style={{
                  background: "rgba(20,20,24,0.85)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.65)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <X className="w-3 h-3" strokeWidth={2} />
              </button>
            </div>
          );
        })}
        {/* Add row: sits at the bottom of the grid, matching the row height. */}
        <button
          type="button"
          onClick={addIngredient}
          data-testid="sim-add-ingredient"
          className="w-full px-4 py-3 flex items-center justify-center gap-2 text-xs text-white/55 hover:text-white/90 hover:bg-white/[0.03] transition-colors border-t"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
        >
          <Plus className="w-3.5 h-3.5" style={{ color: "rgba(232,207,154,0.85)" }} />
          <span className="uppercase tracking-[0.2em] text-[0.6rem]">Add ingredient</span>
        </button>
          </div>
        </div>
      </div>
        </div>
      </div>

      {/* Production Scaler slider */}
      <div className="mt-6 rounded-2xl px-5 py-4" style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
        border: "1px solid rgba(255,255,255,0.08)",
      }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[0.55rem] uppercase tracking-[0.24em] text-white/60 flex items-center gap-1.5">
            <Zap className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Production scaler
          </div>
          <div className="tabular-nums text-sm">
            <span className="font-display text-2xl text-white/95" data-testid="portions-value">{portions}</span>
            <span className="text-white/50 text-xs ml-1">portion{portions === 1 ? "" : "s"}</span>
          </div>
        </div>
        <Slider
          value={[portions]}
          onValueChange={(v) => setPortions(v[0])}
          min={1}
          max={500}
          step={1}
          data-testid="portions-slider"
          className="my-2"
        />
        <div className="flex justify-between text-[0.6rem] text-white/40 mt-2 tabular-nums">
          <span>1</span>
          <span>50</span>
          <span>100</span>
          <span>250</span>
          <span>500</span>
        </div>

        {/* Batch totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          <div>
            <div className="text-[0.55rem] uppercase tracking-[0.24em] text-white/45">Food cost</div>
            <div className="font-display text-xl text-white/90 tabular-nums mt-1">
              <AnimatedNumber value={totalCost} decimals={2} prefix="$" testId="batch-food-cost" />
            </div>
          </div>
          <div>
            <div className="text-[0.55rem] uppercase tracking-[0.24em] text-white/45">Revenue @ ${SIGNATURE_RECIPE.sell_price.toFixed(0)}/portion</div>
            <div className="font-display text-xl text-white/90 tabular-nums mt-1">
              <AnimatedNumber value={totalRevenue} decimals={2} prefix="$" testId="batch-revenue" />
            </div>
          </div>
          <div>
            <div className="text-[0.55rem] uppercase tracking-[0.24em]" style={{ color: "rgba(232,207,154,0.85)" }}>Margin captured</div>
            <div className="font-display text-xl tabular-nums mt-1" style={{ color: "#f2dfb5" }}>
              <AnimatedNumber value={marginDollars} decimals={2} prefix="$" testId="batch-margin" />
            </div>
          </div>
        </div>
      </div>

      {/* Primary CTA */}
      <div className="mt-8 flex flex-col items-center gap-3">
        <Link
          to="/register?carry=1"
          data-testid="start-full-ops-cta"
          className="gold-cta group relative overflow-hidden px-8 h-12 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm w-full sm:w-auto sm:min-w-[280px]"
        >
          {isCustomised ? "Save This Recipe" : "Start Full Operations"}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <p className="text-[0.65rem] text-white/40 uppercase tracking-[0.24em] text-center">
          {isCustomised
            ? "Your recipe carries into a new account : 14-day trial : no card"
            : "14-day trial : no card required"}
        </p>
      </div>
    </GlassPanel>
  );
}

// --------------------------------------------------------------------------
// Landing: the public-facing marquee page. Hero + Simulator + trust badges.
// --------------------------------------------------------------------------
function Pillar({ icon: Icon, eyebrow, title, body }) {
  return (
    <div
      className="rounded-2xl p-6 md:p-7 h-full"
      style={{
        background: "linear-gradient(180deg, rgba(20,17,12,0.72), rgba(11,11,13,0.85))",
        border: "1px solid rgba(232,207,154,0.18)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
      data-testid={`pillar-${eyebrow.toLowerCase()}`}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center mb-5"
        style={{
          background: "linear-gradient(135deg, rgba(232,207,154,0.28), rgba(200,169,106,0.06))",
          border: "1px solid rgba(232,207,154,0.4)",
        }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.6} style={{ color: "#f2dfb5" }} />
      </div>
      <div className="text-[0.55rem] uppercase tracking-[0.32em]" style={{ color: "rgba(232,207,154,0.85)" }}>
        {eyebrow}
      </div>
      <h3 className="font-display text-xl md:text-2xl mt-2 leading-snug">{title}</h3>
      <p className="text-sm text-white/60 mt-3 leading-relaxed">{body}</p>
    </div>
  );
}

function FeaturePanel({ eyebrow, icon: Icon, title, body, bullets, reverse = false }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
      <div>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center mb-4"
          style={{
            background: "linear-gradient(135deg, rgba(232,207,154,0.28), rgba(200,169,106,0.06))",
            border: "1px solid rgba(232,207,154,0.4)",
          }}
        >
          <Icon className="w-4 h-4" strokeWidth={1.6} style={{ color: "#f2dfb5" }} />
        </div>
        <div className="text-[0.55rem] uppercase tracking-[0.32em]" style={{ color: "rgba(232,207,154,0.85)" }}>
          {eyebrow}
        </div>
        <h3 className="font-display text-3xl md:text-4xl mt-2 leading-tight tracking-tight">{title}</h3>
        <p className="text-white/60 mt-4 leading-relaxed">{body}</p>
      </div>
      <ul className="space-y-3">
        {bullets.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-xl p-4"
            style={{
              background: "rgba(232,207,154,0.04)",
              border: "1px solid rgba(232,207,154,0.14)",
            }}
          >
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#f2dfb5" }} />
            <span className="text-sm text-white/80">{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MarginAudit() {
  // A specialist hook: chef enters weekly revenue + current food-cost %.
  // We estimate the *hidden* margin they're likely losing to yield + waste + prep-loss blindspots.
  // Conservative industry range: 5 to 8 percent of revenue. Use 6% as a midpoint, 8% for high-FC kitchens.
  const [revenue, setRevenue] = useState(24000);
  const [foodCost, setFoodCost] = useState(34);
  const [ran, setRan] = useState(false);
  const [currency, setCurrency] = useState("USD");
  useEffect(() => { setCurrency(detectCurrency()); }, []);

  // Industry benchmark ceiling on hidden yield loss, keyed to current FC%.
  const industryLeakPct = useMemo(() => {
    const fc = Number(foodCost) || 0;
    if (fc >= 40) return 0.08;
    if (fc >= 32) return 0.06;
    return 0.05;
  }, [foodCost]);

  // Recoverable is capped by the headroom above the industry-healthy floor
  // of 28%. A kitchen already at 28% has nothing structural to recover, so
  // the audit shouldn't overpromise. This keeps every number internally
  // consistent: currentFC - targetFC == recoverable%.
  const fc = Math.max(0, Number(foodCost) || 0);
  const rev = Math.max(0, Number(revenue) || 0);
  const headroomPct = Math.max(0, fc - 28) / 100;
  const recoverablePct = Math.min(industryLeakPct, headroomPct);
  const weeklyLeak = rev * recoverablePct;
  const yearlyLeak = weeklyLeak * 52;
  const monthlyLeak = yearlyLeak / 12;
  const currentFoodCost$ = rev * (fc / 100);
  const targetFoodCost = fc - recoverablePct * 100;

  const fmt = (v) => Number(v).toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 0 });

  return (
    <div
      className="rounded-3xl p-6 md:p-10"
      style={{
        background:
          "radial-gradient(circle at 20% 0%, rgba(232,207,154,0.10), transparent 60%), linear-gradient(180deg, rgba(20,17,12,0.75), rgba(11,11,13,0.9))",
        border: "1px solid rgba(232,207,154,0.28)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 30px 80px -30px rgba(0,0,0,0.7)",
      }}
      data-testid="margin-audit"
    >
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, rgba(232,207,154,0.28), rgba(200,169,106,0.06))",
            border: "1px solid rgba(232,207,154,0.4)",
          }}
        >
          <Calculator className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color: "#f2dfb5" }} />
        </div>
        <div className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ color: "rgba(232,207,154,0.85)" }}>
          Free Margin Audit : 30 seconds
        </div>
      </div>
      <h2 className="font-display text-3xl md:text-5xl leading-tight mt-3 tracking-tight">
        See the money hiding in your kitchen.
      </h2>
      <p className="text-white/60 mt-3 max-w-xl text-sm md:text-base">
        Industry data shows most kitchens leak 5 to 8 percent of revenue to unmeasured yield, prep loss and waste. Enter your numbers and we'll estimate what that leak is costing <em>your</em> restaurant.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <div>
          <label className="block text-[0.55rem] uppercase tracking-[0.28em] text-white/50 mb-2">
            Weekly revenue ({currency})
          </label>
          <input
            type="number"
            value={revenue}
            onChange={(e) => { setRevenue(e.target.value); setRan(false); }}
            className="w-full h-12 px-4 rounded-lg text-lg tabular font-medium"
            style={{
              background: "rgba(0,0,0,0.5)",
              border: "1px solid rgba(232,207,154,0.25)",
              color: "#f5efe0",
            }}
            data-testid="audit-revenue"
          />
        </div>
        <div>
          <label className="block text-[0.55rem] uppercase tracking-[0.28em] text-white/50 mb-2">
            Current food cost %
          </label>
          <div className="relative">
            <input
              type="number"
              value={foodCost}
              onChange={(e) => { setFoodCost(e.target.value); setRan(false); }}
              className="w-full h-12 px-4 pr-8 rounded-lg text-lg tabular font-medium"
              style={{
                background: "rgba(0,0,0,0.5)",
                border: "1px solid rgba(232,207,154,0.25)",
                color: "#f5efe0",
              }}
              data-testid="audit-foodcost"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 text-sm">%</span>
          </div>
        </div>
      </div>

      <button
        onClick={() => setRan(true)}
        className="gold-cta mt-6 h-12 px-8 rounded-lg inline-flex items-center gap-2 text-sm font-semibold"
        data-testid="audit-run"
      >
        <Calculator className="w-4 h-4" /> Run my audit
      </button>

      {ran && (
        <div className="mt-8 space-y-4" data-testid="audit-result">
          <div
            className="rounded-2xl p-5"
            style={{
              background: "linear-gradient(180deg, rgba(220,120,90,0.10), rgba(220,120,90,0.02))",
              border: "1px solid rgba(220,120,90,0.35)",
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" style={{ color: "#e8a890" }} />
              <div className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: "#e8a890" }}>
                Estimated leak
              </div>
            </div>
            <div className="font-display text-4xl md:text-6xl tracking-tight" style={{ color: "#e8a890" }}>
              {fmt(yearlyLeak)}<span className="text-lg text-white/50 ml-2">/year</span>
            </div>
            <div className="mt-2 text-sm text-white/60">
              At your current food-cost %, industry data suggests you're bleeding <span className="text-white/90 tabular">{fmt(weeklyLeak)}</span> a week to hidden yield and waste. That's <span className="text-white/90 tabular">{fmt(monthlyLeak)}</span> a month.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <AuditStat label="Current FC $" value={fmt(currentFoodCost$)} />
            <AuditStat label="Target FC %" value={`${targetFoodCost.toFixed(1)}%`} highlight />
            <AuditStat label="Recoverable" value={`${(recoverablePct * 100).toFixed(1)}%`} />
          </div>

          <div className="rounded-xl p-5" style={{ background: "rgba(232,207,154,0.05)", border: "1px solid rgba(232,207,154,0.22)" }}>
            <div className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: "rgba(232,207,154,0.85)" }}>
              How Culinaire Specialist recovers it
            </div>
            <ul className="text-sm text-white/75 mt-3 space-y-1.5 leading-relaxed list-disc pl-5 marker:text-[rgba(232,207,154,0.6)]">
              <li>Yield-adjusted usable cost on every ingredient, so your recipes cost against reality, not the invoice.</li>
              <li>One-tap waste logging with reason tags: burnt, dropped, sent back, expired.</li>
              <li>Sunday-night digest that surfaces the leak before month-end.</li>
              <li>Quarterly menu snapshots that prove the recovery, plate by plate.</li>
            </ul>
            <Link
              to="/register"
              className="gold-cta mt-5 h-11 px-5 rounded-lg inline-flex items-center gap-2 text-sm font-semibold"
              data-testid="audit-cta-register"
            >
              Recover it in a 14-day trial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditStat({ label, value, highlight }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: highlight ? "rgba(120,180,120,0.08)" : "rgba(0,0,0,0.35)",
        border: `1px solid ${highlight ? "rgba(120,180,120,0.32)" : "rgba(255,255,255,0.08)"}`,
      }}
    >
      <div className="text-[0.55rem] uppercase tracking-[0.28em] text-white/50">{label}</div>
      <div
        className={`font-display text-2xl tabular mt-1 ${highlight ? "" : "text-white/95"}`}
        style={highlight ? { color: "#a8d8a8" } : undefined}
      >
        {value}
      </div>
    </div>
  );
}



export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAuthed = user && user !== false;
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);

  // Capture ?ref=CODE from the URL so it survives navigation to /register.
  useEffect(() => {
    const code = (searchParams.get("ref") || "").trim().toUpperCase();
    if (code) localStorage.setItem(REF_STORAGE_KEY, code);
  }, [searchParams]);

  return (
    <div
      className="min-h-screen text-white/95"
      style={{ backgroundColor: "#000000" }}
      data-testid="landing-page"
    >
      {/* Ambient radial glows on pure OLED black */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: [
            "radial-gradient(1200px 600px at 15% -10%, rgba(232,207,154,0.10), transparent 60%)",
            "radial-gradient(1000px 500px at 90% 30%, rgba(90,178,201,0.06), transparent 60%)",
            "radial-gradient(800px 400px at 50% 110%, rgba(232,207,154,0.05), transparent 60%)",
          ].join(","),
        }}
      />

      {/* Top nav */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <img
            src="/brand/mark-256.png"
            alt="CulinaireSpecialist"
            className="w-10 h-10 rounded-full object-contain"
            style={{ background: "#0a0806", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 0 0 1px rgba(232,207,154,0.35)" }}
          />
          <div>
            <div className="font-display text-lg leading-tight">CulinaireSpecialist</div>
            <div className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: "rgba(232,207,154,0.85)" }}>
              Precision cost engine
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4 text-sm">
          {isAuthed ? (
            <Link
              to="/dashboard"
              className="gold-cta px-4 h-10 rounded-lg flex items-center gap-1.5 font-semibold text-xs"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-3.5 h-3.5" /> Go to dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="px-3 py-2 text-white/70 hover:text-white transition-colors" data-testid="nav-signin">
                Sign in
              </Link>
              <Link
                to="/register"
                className="gold-cta px-4 h-10 rounded-lg flex items-center gap-1.5 font-semibold text-xs"
                data-testid="nav-getstarted"
              >
                Get started <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-16 pb-10 md:pb-20">
        <div className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ color: "rgba(232,207,154,0.85)" }}>
          The standard for precision culinary operations
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.98] mt-4 tracking-tight max-w-4xl">
          Surgical <span style={{ color: "#f2dfb5" }}>precision</span> for your margins.<br className="hidden md:block" /> More time for your craft.
        </h1>
        <p className="text-white/60 mt-6 max-w-2xl text-base md:text-lg">
          100% accurate recipe costing, yield-adjusted inventory, and automated invoice processing for the global culinary industry. Stop losing hours to manual spreadsheets: reclaim the margin hidden in your kitchen.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-8 text-xs text-white/50">
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" style={{ color: "#f2dfb5" }} /> 100% accurate unit math</span>
          <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" style={{ color: "#f2dfb5" }} /> Yield-adjusted inventory</span>
          <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" style={{ color: "#f2dfb5" }} /> Automated invoice AI</span>
        </div>
      </section>

      {/* Simulator */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <LiveCostingSimulator />
      </section>

      {/* Why it matters: three pillars */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20" data-testid="why-it-matters">
        <div className="text-[0.6rem] uppercase tracking-[0.32em] text-center" style={{ color: "rgba(232,207,154,0.85)" }}>
          Why it matters
        </div>
        <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3 text-center max-w-3xl mx-auto">
          Three things every serious kitchen owes itself.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-12">
          <Pillar
            icon={Crosshair}
            eyebrow="Accuracy"
            title="Eliminate the 5 to 8 percent margin leak hidden in your yield and waste."
            body="Whole-fish trim, artichoke prep loss, oil-by-litre-served-by-gram: every hidden loss is reconciled by the engine. Real numbers, real margin."
          />
          <Pillar
            icon={Clock}
            eyebrow="Time"
            title="Automate the administrative heavy lifting: invoice to inventory in seconds."
            body="Photograph a supplier invoice, watch the AI extract line items, map them to your ingredients and stock the pantry. What took an hour takes a minute."
          />
          <Pillar
            icon={Eye}
            eyebrow="Certainty"
            title="Total operational visibility for Executive Chefs and Business Owners."
            body="Sunday-night cost digest, quarter-over-quarter menu snapshots and a live labour ledger. Know the number before the accountant does."
          />
        </div>
      </section>

      {/* Sitelink anchor: Precision Costing */}
      <section id="precision-costing" className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-24 scroll-mt-24" data-testid="precision-costing">
        <FeaturePanel
          eyebrow="Precision Costing"
          icon={Calculator}
          title="100% accurate to the gram, drop, and dollar."
          body="Every recipe converted across weight, volume and count. Yield-adjusted purchase prices. Labour split by batch. When you tap Save, the number is right. Not close, not approximate. Right."
          bullets={[
            "Weight ↔ volume conversion via ingredient density",
            "Yield-adjusted usable-cost per pack",
            "Batch labour distributed across servings",
            "Currency and locale detected automatically",
          ]}
        />
      </section>

      {/* Sitelink anchor: Automated Inventory */}
      <section id="automated-inventory" className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-24 scroll-mt-24" data-testid="automated-inventory">
        <FeaturePanel
          eyebrow="Automated Inventory"
          icon={Boxes}
          title="Invoice-to-inventory in seconds, not spreadsheets."
          body="Snap a supplier invoice. The AI reads every line item, matches suppliers, updates purchase prices and increments your pantry. Every sale automatically deducts the yield-adjusted quantities on the back-end."
          bullets={[
            "AI extraction from photos and PDFs",
            "Supplier-mapping memory across invoices",
            "Auto-deduct on every recorded sale",
            "Live low-stock alerts before you 86",
          ]}
          reverse
        />
      </section>

      {/* Sitelink anchor: Culinary Insights */}
      <section id="culinary-insights" className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-24 scroll-mt-24" data-testid="culinary-insights">
        <FeaturePanel
          eyebrow="Culinary Insights"
          icon={LineChart}
          title="AI-driven signal, not spreadsheet noise."
          body="Weekly Cost Digest, quarterly Menu Snapshots, and Michelin-style plating critique on any dish photo. The system tells you where the margin is moving, and why, before it costs you a season."
          bullets={[
            "Sunday-night cost digest by email",
            "Menu snapshots with quarter-over-quarter deltas",
            "AI plating coach on any hero photograph",
            "Waste tagged by reason for process signal",
          ]}
        />
      </section>

      {/* Free Margin Audit tool */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-24">
        <MarginAudit />
      </section>

      {/* ================================================================
          Dual-Launch section: two distinct paths, one page.
          Independent operators go to trial signup.
          Multi-location groups open the audit request modal.
          ================================================================ */}
      <section
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 grid md:grid-cols-2 gap-6"
        data-testid="dual-launch"
      >
        {/* Independent Path: Invoice Scanner spotlight */}
        <div
          className="rounded-[28px] relative overflow-hidden flex flex-col"
          style={{
            background: "linear-gradient(180deg, rgba(24,17,10,0.75), rgba(14,10,7,0.85))",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(232,207,154,0.28)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 30px 80px -20px rgba(0,0,0,0.55)",
          }}
          data-testid="independent-path"
        >
          {/* Premium image header: hands at work over a fresh ticket */}
          <div className="relative h-56 md:h-64 overflow-hidden" data-testid="independent-hero-image">
            <img
              src="https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=1600&q=85"
              alt="Chef reviewing a delivery invoice at a marble kitchen pass"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              style={{ filter: "saturate(0.9) contrast(1.05)" }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "linear-gradient(180deg, rgba(14,10,7,0.10) 0%, rgba(14,10,7,0.55) 55%, rgba(14,10,7,0.95) 100%)",
              }}
            />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.55rem] uppercase tracking-[0.32em]"
              style={{
                background: "rgba(10,8,6,0.55)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(232,207,154,0.35)",
                color: "rgba(232,207,154,0.95)",
              }}
            >
              <Rocket className="w-3 h-3" /> Independent Operators
            </div>
          </div>

          <div className="p-8 md:p-10 relative">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(232,207,154,0.18), transparent 65%)" }} />

            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">
              Point your camera at any invoice. Costing done.
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-md mb-6">
              The Invoice Scanner reads line items, prices and units from a photo in about ninety seconds. No typing. No spreadsheet copy paste. Your ingredient prices refresh the moment the delivery arrives.
            </p>

            <ul className="space-y-2.5 mb-8 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <ScanLine className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "rgba(232,207,154,0.9)" }} />
                <span>Photo to structured data in one tap. GPT-4o vision reads even faded thermal paper.</span>
              </li>
              <li className="flex items-start gap-2">
                <Boxes className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "rgba(232,207,154,0.9)" }} />
                <span>Auto-links each line to your pantry so recipe cost updates on the spot.</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "rgba(232,207,154,0.9)" }} />
                <span>Review, edit, accept. Nothing writes to your books without your confirmation.</span>
              </li>
            </ul>

            <button
              type="button"
              onClick={() => navigate("/register")}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all"
              style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.42), rgba(200,169,106,0.16))",
                border: "1px solid rgba(232,207,154,0.58)",
                color: "#f5efe0",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 10px 30px -12px rgba(200,169,106,0.4)",
              }}
              data-testid="cta-independent-trial"
            >
              Start 14-day trial: no credit card required <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Enterprise Path: Strategic Solutions + Multi-Location ROI */}
        <div
          className="rounded-[28px] relative overflow-hidden flex flex-col"
          style={{
            background: "linear-gradient(180deg, rgba(18,14,9,0.78), rgba(10,8,6,0.88))",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(232,207,154,0.22)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 30px 80px -20px rgba(0,0,0,0.55)",
          }}
          data-testid="enterprise-path"
        >
          {/* Premium image header: elegant fine-dining room */}
          <div className="relative h-56 md:h-64 overflow-hidden" data-testid="enterprise-hero-image">
            <img
              src="https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1600&q=85"
              alt="Elegant fine-dining room lit by warm brass fixtures at service"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              style={{ filter: "saturate(0.92) contrast(1.08)" }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "linear-gradient(180deg, rgba(10,8,6,0.15) 0%, rgba(10,8,6,0.55) 55%, rgba(10,8,6,0.95) 100%)",
              }}
            />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.55rem] uppercase tracking-[0.32em]"
              style={{
                background: "rgba(10,8,6,0.55)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(232,207,154,0.35)",
                color: "rgba(232,207,154,0.95)",
              }}
            >
              <Building2 className="w-3 h-3" /> Strategic Solutions : Multi-Location
            </div>
          </div>

          <div className="p-8 md:p-10 relative">
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(232,207,154,0.14), transparent 65%)" }} />

            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">
              One dashboard. Every site. True ROI.
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-md mb-6">
              For hospitality groups running three or more locations. We audit your current cost, labor and inventory workflow, then deploy the same precision engine across every site with role-based access, per-location reporting and consolidated group margin views.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              <MiniStat label="Locations synced" value="Unlimited" />
              <MiniStat label="Deploy in" value="14 days" />
              <MiniStat label="Group margin lift" value="4 to 7 points" />
            </div>

            <button
              type="button"
              onClick={() => setEnterpriseOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all"
              style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.1))",
                border: "1px solid rgba(232,207,154,0.5)",
                color: "#f5efe0",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12), 0 10px 30px -12px rgba(200,169,106,0.3)",
              }}
              data-testid="cta-request-audit"
            >
              Request Operational Audit <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Security & Precision: full-width card under the two paths */}
        <div
          className="md:col-span-2 rounded-[28px] p-8 md:p-10 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, rgba(16,12,8,0.7), rgba(8,6,4,0.82))",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(232,207,154,0.18)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 30px 80px -20px rgba(0,0,0,0.5)",
          }}
          data-testid="security-card"
        >
          <div className="text-[0.55rem] uppercase tracking-[0.32em] mb-3 flex items-center gap-2" style={{ color: "rgba(232,207,154,0.85)" }}>
            <ShieldCheck className="w-3 h-3" /> Security and Precision
          </div>
          <h3 className="font-display text-3xl md:text-4xl leading-tight mb-4 max-w-2xl">
            100 percent database isolation. Secure stack from prep to plate.
          </h3>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <SecurityPoint
              icon={Database}
              title="Isolated per tenant"
              body="Every hospitality group runs against its own logical database boundary. No shared tables, no cross-account leakage."
            />
            <SecurityPoint
              icon={Lock}
              title="Encrypted at rest and in transit"
              body="TLS 1.3 on the wire. Sensitive fields such as POS access tokens sit behind Fernet encryption keyed to your secret."
            />
            <SecurityPoint
              icon={MapPin}
              title="Auditable and deterministic"
              body="Every cost calculation is reproducible. Snapshots freeze recipe cost and margin for every quarterly close."
            />
          </div>
        </div>
      </section>

      {/* Bottom trust strip */}
      <footer className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 text-xs text-white/40 text-center space-y-2">
        <div>Built for chefs who cost every gram. Every calculation is deterministic, transparent, and reproducible.</div>
        <div className="flex items-center justify-center gap-4 text-white/50">
          <a href="/blog" className="hover:gold transition-colors">Journal</a>
          <a href="/pricing" className="hover:gold transition-colors" data-testid="footer-pricing-link">Pricing</a>
          <span className="text-white/20">•</span>
          <a href="/press" className="hover:gold transition-colors">Press</a>
          <span className="text-white/20">•</span>
          <a href="/trust" className="hover:gold transition-colors inline-flex items-center gap-1" data-testid="footer-trust-link">
            <ShieldCheck className="w-3 h-3" /> Trust
          </a>
        </div>
      </footer>

      <EnterpriseAuditModal open={enterpriseOpen} onClose={() => setEnterpriseOpen(false)} />
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        background: "rgba(0,0,0,0.32)",
        border: "1px solid rgba(232,207,154,0.14)",
      }}
    >
      <div className="text-[0.5rem] uppercase tracking-[0.24em] text-white/45">{label}</div>
      <div className="font-display text-lg md:text-xl mt-1 tabular" style={{ color: "#f2dfb5" }}>{value}</div>
    </div>
  );
}

function SecurityPoint({ icon: Icon, title, body }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: "rgba(0,0,0,0.32)",
        border: "1px solid rgba(232,207,154,0.14)",
      }}
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
        style={{
          background: "linear-gradient(135deg, rgba(232,207,154,0.22), rgba(200,169,106,0.06))",
          border: "1px solid rgba(232,207,154,0.32)",
        }}
      >
        <Icon className="w-4 h-4" style={{ color: "#f2dfb5" }} />
      </div>
      <div className="font-display text-lg leading-tight mb-1.5">{title}</div>
      <div className="text-xs text-white/60 leading-relaxed">{body}</div>
    </div>
  );
}
